import React, { useEffect, useRef, useState } from "react";
import { Trash2, Plus, Shield, Upload, RefreshCcw, BarChart3, Film, Tv, Users, Clock, Star, X, Radio, Award, User2, Check, Ban, Search as SearchIcon, KeyRound, Mail, Crown, Clapperboard, Bell, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { WEEKDAYS } from "./shared";

/* ---------------- GCR Theaters Admin ---------------- */

export default function GCRTheaters() {
  const [subtab, setSubtab] = useState("channels");
  const [channels, setChannels] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/gcr/channels");
      setChannels(data || []);
    } catch (e) { toast.error("Failed to load channels"); }
  };
  useEffect(() => { refresh(); }, []);

  return (
    <div>
      <div className="flex items-center gap-2 mb-6 border-b border-white/10">
        {[
          { id: "channels", label: "Channels", Icon: Radio },
          { id: "ads", label: "Banner ads", Icon: Star },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`gcr-subtab-${t.id}`}
            onClick={() => setSubtab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
              subtab === t.id ? "border-[#E50914] text-white" : "border-transparent text-gray-400 hover:text-white"
            }`}
          >
            <t.Icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {subtab === "ads" && <GCRAdsAdmin />}
      {subtab !== "channels" ? null : (
      <>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold flex items-center gap-2"><Radio className="w-5 h-5 text-[#E50914]" /> GCR Theaters Channels</h2>
        <button data-testid="gcr-new-channel-btn" onClick={() => setShowNew(true)} className="btn-red px-4 py-2 rounded text-sm font-semibold flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Channel
        </button>
      </div>

      {channels.length === 0 && (
        <div className="bg-[#0f0f0f] border border-white/5 rounded-lg p-8 text-center text-gray-400">
          No channels yet. Click <strong>New Channel</strong> to create your first one.
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2 mb-6">
        {channels.map((c) => (
          <div key={c.channel_id} data-testid={`gcr-channel-${c.slug}`} className="bg-[#0f0f0f] border border-white/5 rounded-lg p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded flex items-center justify-center font-black flex-shrink-0" style={{ background: c.color || "#E50914" }}>{c.name?.[0] || "?"}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{c.name}</div>
              <div className="text-xs text-gray-500 mt-0.5 truncate">/{c.slug} · {(c.allowed_countries || []).length ? `Allowed: ${c.allowed_countries.join(",")}` : "Worldwide"}{(c.blocked_countries || []).length ? ` · Blocked: ${c.blocked_countries.join(",")}` : ""}</div>
            </div>
            <button data-testid={`gcr-edit-${c.slug}`} onClick={() => setEditing(c)} className="btn-glass px-3 py-1.5 rounded text-xs">Schedule</button>
            <button
              data-testid={`gcr-delete-${c.slug}`}
              onClick={async () => {
                if (!confirm(`Delete channel ${c.name}? All schedules will be removed.`)) return;
                await api.delete(`/admin/gcr/channels/${c.channel_id}`);
                toast.success("Channel deleted"); refresh();
              }}
              className="text-gray-400 hover:text-red-400 p-2"
              aria-label="delete"
            ><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      {showNew && <ChannelForm onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); refresh(); }} />}
      {editing && <ChannelScheduleEditor channel={editing} onClose={() => setEditing(null)} />}
      </>
      )}
    </div>
  );
}

function ChannelForm({ channel, onClose, onSaved }) {
  const [form, setForm] = useState(channel || { name: "", slug: "", description: "", color: "#E50914", logo: "", allowed_countries: [], blocked_countries: [] });
  const [saving, setSaving] = useState(false);

  // ----- Initial first-show scheduling (only on CREATE, not edit) -----
  const isEdit = !!channel;
  const [withSchedule, setWithSchedule] = useState(false);
  const todayLocal = new Date();
  todayLocal.setMinutes(todayLocal.getMinutes() + 5);
  const defaultDate = todayLocal.toISOString().slice(0, 10);
  const defaultTime = todayLocal.toISOString().slice(11, 16);
  const [sch, setSch] = useState({
    mode: "url",           // "url" | "upload" | "existing"
    title: "",
    stream_url: "",
    video_id: "",          // when picking an existing video
    date: defaultDate,     // YYYY-MM-DD (interpreted as UTC)
    time: defaultTime,     // HH:MM UTC
    duration_min: 60,
  });
  const [uploadName, setUploadName] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoOptions, setVideoOptions] = useState([]);
  useEffect(() => {
    if (withSchedule && sch.mode === "existing" && videoOptions.length === 0) {
      api.get("/admin/videos").then(r => setVideoOptions(r.data || [])).catch(() => {});
    }
  }, [withSchedule, sch.mode]);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploadName(file.name);
    setUploadProgress(1);
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload?kind=video", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        const pct = Math.round((e.loaded / (e.total || 1)) * 100);
        setUploadProgress(Math.max(1, pct));
      },
    });
    setSch((s) => ({ ...s, stream_url: data.url }));
    setUploadProgress(100);
    toast.success("Upload complete");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...form,
        allowed_countries: typeof form.allowed_countries === "string" ? form.allowed_countries.split(",").map(s => s.trim()).filter(Boolean) : (form.allowed_countries || []),
        blocked_countries: typeof form.blocked_countries === "string" ? form.blocked_countries.split(",").map(s => s.trim()).filter(Boolean) : (form.blocked_countries || []),
      };
      if (channel) {
        await api.patch(`/admin/gcr/channels/${channel.channel_id}`, body);
        toast.success("Channel updated");
        onSaved();
        return;
      }
      // CREATE: channel first, then (optionally) an initial schedule
      await api.post("/admin/gcr/channels", body);
      if (withSchedule) {
        let vid = sch.video_id;
        // If url / upload mode → create a video row first
        if (sch.mode === "url" || sch.mode === "upload") {
          if (!sch.stream_url) throw new Error(sch.mode === "upload" ? "Upload a file first" : "Provide a streaming URL");
          if (!sch.title) throw new Error("Give the first show a title");
          const newVideo = {
            title: sch.title,
            type: "movie",
            genres: [],
            year: new Date().getFullYear(),
            duration_min: Number(sch.duration_min) || 60,
            rating: "TV-14",
            tmdb_rating: 0,
            description: `Premiere broadcast on ${body.name}`,
            poster: body.logo || "https://image.tmdb.org/t/p/w500/49WJfeN0moxb9IPfGn8AIqMGskD.jpg",
            backdrop: body.logo || "",
            stream_url: sch.stream_url,
            cast: [],
            subtitles: [],
            audio_tracks: [],
          };
          const { data: created } = await api.post("/admin/videos", newVideo);
          vid = created.id;
        }
        if (!vid) throw new Error("Pick or upload a show first");
        await api.post("/admin/gcr/schedules", {
          channel_slug: body.slug.toLowerCase().trim(),
          video_id: vid,
          start_time: sch.time,
          weekdays: [],
          date: sch.date,
          duration_min: Number(sch.duration_min) || 60,
          ticket_price: 0,
          ticket_currency: "usd",
          premium_free: true,
          on_demand_unlock: false,
        });
        toast.success(`Channel created · first show on ${sch.date} ${sch.time} UTC`);
      } else {
        toast.success("Channel created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Save failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#0f0f0f] border border-white/10 rounded-lg p-6 w-full max-w-2xl space-y-4 my-8">
        <h3 className="text-lg font-semibold">{channel ? "Edit Channel" : "New Channel"}</h3>
        <input data-testid="gcr-form-name" required placeholder="Channel name (e.g. GCR Action)" className="input-dark" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input data-testid="gcr-form-slug" required placeholder="slug (lowercase, no spaces)" className="input-dark" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} />
        <input placeholder="Logo URL (optional)" className="input-dark" value={form.logo || ""} onChange={(e) => setForm({ ...form, logo: e.target.value })} />
        <textarea placeholder="Description" className="input-dark" rows="2" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500">Brand color</label>
            <input type="color" className="w-full h-10 bg-transparent rounded" value={form.color || "#E50914"} onChange={(e) => setForm({ ...form, color: e.target.value })} />
          </div>
          <div className="col-span-1">
            <label className="text-xs text-gray-500">Allowed countries (ISO-2, comma-sep, blank = all)</label>
            <input className="input-dark" placeholder="e.g. US,CA,GB" value={Array.isArray(form.allowed_countries) ? form.allowed_countries.join(",") : form.allowed_countries} onChange={(e) => setForm({ ...form, allowed_countries: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500">Blocked countries (ISO-2, comma-sep)</label>
          <input className="input-dark" placeholder="e.g. CN,IR" value={Array.isArray(form.blocked_countries) ? form.blocked_countries.join(",") : form.blocked_countries} onChange={(e) => setForm({ ...form, blocked_countries: e.target.value })} />
        </div>

        {!isEdit && (
          <div className="border-t border-white/10 pt-4 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                data-testid="gcr-form-with-schedule"
                type="checkbox"
                className="accent-[#E50914]"
                checked={withSchedule}
                onChange={(e) => setWithSchedule(e.target.checked)}
              />
              <span className="text-sm font-semibold text-[#f6c34a]">Schedule the first show (when this channel goes live)</span>
            </label>
            {withSchedule && (
              <div className="rounded-lg border border-[#f6c34a]/20 bg-[#f6c34a]/[0.04] p-4 space-y-3">
                {/* Mode picker */}
                <div className="flex gap-2 text-xs">
                  {[["url", "Paste link"], ["upload", "Upload file"], ["existing", "Pick existing"]].map(([k, label]) => (
                    <button
                      key={k}
                      type="button"
                      data-testid={`gcr-sch-mode-${k}`}
                      onClick={() => setSch((s) => ({ ...s, mode: k }))}
                      className={`px-3 py-1.5 rounded font-semibold transition border ${
                        sch.mode === k
                          ? "bg-[#f6c34a] text-black border-[#f6c34a]"
                          : "bg-transparent text-gray-300 border-white/15 hover:text-white"
                      }`}
                    >{label}</button>
                  ))}
                </div>

                {/* Per-mode field */}
                {sch.mode === "url" && (
                  <>
                    <input
                      data-testid="gcr-sch-title"
                      className="input-dark"
                      placeholder="Show title (e.g. Premiere Episode)"
                      value={sch.title}
                      onChange={(e) => setSch({ ...sch, title: e.target.value })}
                    />
                    <input
                      data-testid="gcr-sch-url"
                      className="input-dark"
                      placeholder="Streaming URL (https://… .mp4 / .m3u8)"
                      value={sch.stream_url}
                      onChange={(e) => setSch({ ...sch, stream_url: e.target.value })}
                    />
                  </>
                )}
                {sch.mode === "upload" && (
                  <>
                    <input
                      data-testid="gcr-sch-title"
                      className="input-dark"
                      placeholder="Show title (e.g. Premiere Episode)"
                      value={sch.title}
                      onChange={(e) => setSch({ ...sch, title: e.target.value })}
                    />
                    <label className="block">
                      <div className="rounded-lg border-2 border-dashed border-[#f6c34a]/40 bg-black/40 px-4 py-6 text-center cursor-pointer hover:border-[#f6c34a]/70 transition">
                        <div className="text-sm text-gray-300 font-semibold">
                          {uploadName ? `📄 ${uploadName}` : "Tap to upload video file"}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">.mp4 / .webm / .mov · stored on object storage</div>
                        {uploadProgress > 0 && uploadProgress < 100 && (
                          <div className="mt-3 h-1.5 bg-white/10 rounded overflow-hidden">
                            <div className="h-full bg-[#f6c34a] transition-all" style={{ width: `${uploadProgress}%` }} />
                          </div>
                        )}
                        {uploadProgress === 100 && (
                          <div className="text-xs text-green-400 mt-2">✓ Upload complete</div>
                        )}
                      </div>
                      <input
                        data-testid="gcr-sch-file"
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={(e) => uploadFile(e.target.files?.[0])}
                      />
                    </label>
                  </>
                )}
                {sch.mode === "existing" && (
                  <select
                    data-testid="gcr-sch-video-select"
                    className="input-dark"
                    value={sch.video_id}
                    onChange={(e) => setSch({ ...sch, video_id: e.target.value })}
                  >
                    <option value="">— pick a video —</option>
                    {videoOptions.map((v) => (
                      <option key={v.id} value={v.id}>{v.title} ({v.year})</option>
                    ))}
                  </select>
                )}

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Go-live date (UTC)</label>
                    <input
                      data-testid="gcr-sch-date"
                      type="date"
                      className="input-dark"
                      value={sch.date}
                      onChange={(e) => setSch({ ...sch, date: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Go-live time (UTC)</label>
                    <input
                      data-testid="gcr-sch-time"
                      type="time"
                      className="input-dark"
                      value={sch.time}
                      onChange={(e) => setSch({ ...sch, time: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Duration (min)</label>
                    <input
                      data-testid="gcr-sch-duration"
                      type="number"
                      min="1"
                      className="input-dark"
                      value={sch.duration_min}
                      onChange={(e) => setSch({ ...sch, duration_min: e.target.value })}
                    />
                  </div>
                </div>

                <p className="text-[11px] text-gray-500">
                  The channel will start its first broadcast at <strong>{sch.date} {sch.time} UTC</strong>.
                  Before that time, viewers see the OFF AIR curtains + countdown.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="btn-glass px-4 py-2 rounded text-sm">Cancel</button>
          <button data-testid="gcr-form-save" type="submit" disabled={saving} className="btn-red px-5 py-2 rounded text-sm font-semibold">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

function ChannelScheduleEditor({ channel, onClose }) {
  const [schedules, setSchedules] = useState([]);
  const [videos, setVideos] = useState([]);
  const [form, setForm] = useState({ video_id: "", start_time: "20:00", weekdays: [0, 1, 2, 3, 4, 5, 6], date: "", duration_min: "", ticket_price: "", ticket_currency: "usd", on_demand_unlock: false, premium_free: true });
  const [importing, setImporting] = useState(false);

  const refresh = async () => {
    const { data } = await api.get("/admin/gcr/schedules", { params: { channel_slug: channel.slug } });
    setSchedules(data || []);
  };
  useEffect(() => {
    refresh();
    api.get("/videos", { params: { limit: 200 } }).then((r) => setVideos(r.data || []));
  }, [channel.slug]);

  const add = async () => {
    try {
      await api.post("/admin/gcr/schedules", {
        channel_slug: channel.slug,
        video_id: form.video_id,
        start_time: form.start_time,
        weekdays: form.date ? [] : form.weekdays,
        date: form.date || null,
        duration_min: form.duration_min ? Number(form.duration_min) : null,
        ticket_price: form.ticket_price ? Number(form.ticket_price) : 0,
        ticket_currency: form.ticket_currency || "usd",
        on_demand_unlock: !!form.on_demand_unlock,
        premium_free: !!form.premium_free,
      });
      toast.success("Show scheduled");
      setForm({ ...form, video_id: "" });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Schedule failed"); }
  };

  const del = async (sid) => {
    await api.delete(`/admin/gcr/schedules/${sid}`);
    refresh();
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/gcr/schedules/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} rows${data.errors?.length ? ` · ${data.errors.length} errors` : ""}`);
      if (data.errors?.length) console.warn("CSV errors", data.errors);
      refresh();
    } catch (e) { toast.error("Import failed"); }
    finally { setImporting(false); e.target.value = ""; }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0f0f0f] border border-white/10 rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2"><Radio className="w-5 h-5 text-[#E50914]" /> {channel.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">/{channel.slug} · {schedules.length} scheduled show{schedules.length === 1 ? "" : "s"}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-5">
          {/* Add show */}
          <div className="bg-black/40 border border-white/5 rounded-lg p-4">
            <div className="text-sm font-semibold mb-3">Schedule a show</div>
            <div className="grid grid-cols-12 gap-2 mb-3">
              <select data-testid="gcr-sched-video" className="input-dark col-span-6" value={form.video_id} onChange={(e) => setForm({ ...form, video_id: e.target.value })}>
                <option value="">Pick a title…</option>
                {videos.map((v) => <option key={v.id} value={v.id}>{v.title} ({v.year})</option>)}
              </select>
              <input data-testid="gcr-sched-time" type="time" className="input-dark col-span-3" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
              <input type="number" placeholder="Override duration (min)" className="input-dark col-span-3" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} />
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {WEEKDAYS.map((wd, i) => {
                const on = form.weekdays.includes(i);
                return (
                  <button
                    key={wd} type="button"
                    data-testid={`gcr-wkd-${i}`}
                    onClick={() => setForm({ ...form, weekdays: on ? form.weekdays.filter(x => x !== i) : [...form.weekdays, i].sort() })}
                    className={`px-2.5 py-1 rounded text-xs border ${on ? "bg-[#E50914]/20 border-[#E50914]/50 text-white" : "border-white/10 text-gray-400 hover:text-white"}`}
                  >{wd}</button>
                );
              })}
              <span className="text-xs text-gray-500 ml-2">— or one-shot date:</span>
              <input data-testid="gcr-sched-date" type="date" className="input-dark text-xs px-2 py-1 w-40" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              <button data-testid="gcr-sched-add-btn" type="button" onClick={add} disabled={!form.video_id} className="btn-red px-3 py-1.5 rounded text-xs ml-auto disabled:opacity-40">Add</button>
            </div>
            {/* Premium Event ticketing */}
            <div className="border-t border-white/5 pt-3 mt-3 flex flex-wrap items-center gap-2">
              <span className="text-[11px] uppercase tracking-widest text-gray-500 mr-1">Premium event:</span>
              <span className="text-xs text-gray-400">Ticket price</span>
              <input data-testid="gcr-sched-price" type="number" step="0.01" min="0" placeholder="0.00" className="input-dark text-xs px-2 py-1 w-24" value={form.ticket_price} onChange={(e) => setForm({ ...form, ticket_price: e.target.value })} />
              <select data-testid="gcr-sched-currency" className="input-dark text-xs px-2 py-1 w-20" value={form.ticket_currency} onChange={(e) => setForm({ ...form, ticket_currency: e.target.value })}>
                <option value="usd">USD</option><option value="eur">EUR</option><option value="gbp">GBP</option><option value="inr">INR</option>
              </select>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="checkbox" checked={form.premium_free} onChange={(e) => setForm({ ...form, premium_free: e.target.checked })} />
                Free for Premium subs
              </label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="checkbox" checked={form.on_demand_unlock} onChange={(e) => setForm({ ...form, on_demand_unlock: e.target.checked })} />
                On-demand unlock (24 h after show)
              </label>
            </div>
          </div>

          {/* CSV import */}
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <label className="btn-glass px-3 py-1.5 rounded cursor-pointer flex items-center gap-1.5">
              <Upload className="w-3.5 h-3.5" /> {importing ? "Importing…" : "Import CSV"}
              <input data-testid="gcr-csv-import" type="file" accept=".csv,text/csv" hidden onChange={importCsv} />
            </label>
            <span>columns: <code className="bg-black/40 px-1.5 py-0.5 rounded">channel_slug,video_id,start_time,weekdays,date,duration_min</code></span>
          </div>

          {/* Schedule list */}
          <div className="space-y-1">
            {schedules.length === 0 && <div className="text-sm text-gray-500 text-center py-6">No shows scheduled yet.</div>}
            {schedules.map((s) => {
              const v = videos.find((x) => x.id === s.video_id);
              const isPaid = (s.ticket_price || 0) > 0;
              return (
                <div key={s.schedule_id} className="flex items-center gap-3 bg-black/30 border border-white/5 rounded p-2.5 text-sm">
                  <span className="font-mono text-[#E50914] text-xs w-12">{s.start_time}</span>
                  <span className="flex-1 truncate">{v?.title || s.video_id}</span>
                  {isPaid && (
                    <span className="bg-[#E50914]/15 border border-[#E50914]/40 text-[#E50914] text-[10px] font-bold uppercase tracking-widest rounded px-2 py-0.5">
                      🎟 ${s.ticket_price?.toFixed(2)} {(s.ticket_currency || "usd").toUpperCase()}
                    </span>
                  )}
                  <span className="text-xs text-gray-500">
                    {s.date ? s.date : (s.weekdays || []).map((d) => WEEKDAYS[d]).join(",")} · {s.duration_min}m
                  </span>
                  <button onClick={() => del(s.schedule_id)} className="text-gray-500 hover:text-red-400" aria-label="delete"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- GCR Banner Ads Admin ---------------- */
function GCRAdsAdmin() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/gcr/ads");
      setAds(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load ads");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const remove = async (ad_id) => {
    if (!window.confirm("Delete this ad? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/gcr/ads/${ad_id}`);
      toast.success("Ad deleted");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const toggleActive = async (ad) => {
    try {
      await api.patch(`/admin/gcr/ads/${ad.ad_id}`, { ...ad, is_active: !ad.is_active });
      toast.success(ad.is_active ? "Ad paused" : "Ad activated");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <div data-testid="admin-gcr-ads-panel">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h2 className="text-xl font-semibold flex items-center gap-2"><Star className="w-5 h-5 text-amber-400" /> Banner ads on /live</h2>
        <p className="text-xs text-gray-500 hidden md:block">Rotating sponsored slots. Schedule with start/end dates. Click tracking included.</p>
        <button data-testid="gcr-ad-new-btn" onClick={() => setEditing({ _new: true, title: "", image_url: "", click_url: "", caption: "", position: "top", is_active: true, starts_at: "", ends_at: "", display_order: 0 })} className="ml-auto btn-red px-4 py-2 rounded text-sm font-semibold flex items-center gap-2">
          <Plus className="w-4 h-4" /> New ad
        </button>
      </div>

      {loading && <p className="text-gray-400">Loading…</p>}

      {!loading && ads.length === 0 && (
        <div data-testid="gcr-ads-empty" className="bg-[#0f0f0f] border border-white/5 rounded-lg p-10 text-center">
          <Star className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-1">No banner ads yet</h3>
          <p className="text-sm text-gray-500 mb-4">Add an image, choose a slot (top/middle/bottom), and it'll go live on the GCR Theaters page.</p>
        </div>
      )}

      {!loading && ads.length > 0 && (
        <div className="space-y-2">
          {ads.map((ad) => (
            <div key={ad.ad_id} data-testid={`gcr-ad-row-${ad.ad_id}`} className="bg-[#0f0f0f] border border-white/5 hover:border-white/15 rounded-lg p-3 flex items-center gap-4 transition-colors">
              <img src={ad.image_url} alt={ad.title} className="w-32 h-12 object-cover rounded border border-white/10" loading="lazy" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold truncate">{ad.title}</span>
                  <span className="text-[10px] uppercase tracking-widest bg-white/5 border border-white/10 rounded px-1.5 py-0.5">{ad.position}</span>
                  {ad.is_active ? (
                    <span className="text-[10px] uppercase tracking-widest bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded px-1.5 py-0.5">Live</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest bg-white/5 text-gray-500 border border-white/10 rounded px-1.5 py-0.5">Paused</span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 truncate">
                  {ad.click_url ? <span className="text-gray-400">→ {ad.click_url}</span> : <span className="italic">no link</span>}
                  {ad.click_count > 0 && <span className="ml-3 text-amber-400">· {ad.click_count} click{ad.click_count === 1 ? "" : "s"}</span>}
                  {(ad.starts_at || ad.ends_at) && (
                    <span className="ml-3">· {ad.starts_at ? new Date(ad.starts_at).toLocaleDateString() : "now"} → {ad.ends_at ? new Date(ad.ends_at).toLocaleDateString() : "always"}</span>
                  )}
                </div>
              </div>
              <button data-testid={`gcr-ad-toggle-${ad.ad_id}`} onClick={() => toggleActive(ad)} className="btn-glass px-3 py-1.5 rounded text-xs">
                {ad.is_active ? "Pause" : "Activate"}
              </button>
              <button data-testid={`gcr-ad-edit-${ad.ad_id}`} onClick={() => setEditing(ad)} className="btn-glass px-3 py-1.5 rounded text-xs">Edit</button>
              <button data-testid={`gcr-ad-delete-${ad.ad_id}`} onClick={() => remove(ad.ad_id)} className="text-gray-500 hover:text-red-400 p-2" aria-label="delete">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <GCRAdForm
          initial={editing}
          isNew={!!editing._new}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function GCRAdForm({ initial, isNew, onClose, onSaved }) {
  const toLocalDate = (val) => {
    if (!val) return "";
    try { return new Date(val).toISOString().slice(0, 10); } catch { return ""; }
  };
  const [form, setForm] = useState({
    title: initial.title || "",
    image_url: initial.image_url || "",
    click_url: initial.click_url || "",
    caption: initial.caption || "",
    position: initial.position || "top",
    is_active: initial.is_active !== false,
    starts_at: toLocalDate(initial.starts_at),
    ends_at: toLocalDate(initial.ends_at),
    display_order: typeof initial.display_order === "number" ? initial.display_order : 0,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "poster");
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  const onPickFile = (e) => uploadFile(e.target.files?.[0]);

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    uploadFile(e.dataTransfer?.files?.[0]);
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.image_url.trim()) { toast.error("Image is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        starts_at: form.starts_at ? new Date(form.starts_at + "T00:00:00Z").toISOString() : null,
        ends_at: form.ends_at ? new Date(form.ends_at + "T23:59:59Z").toISOString() : null,
      };
      if (isNew) {
        await api.post("/admin/gcr/ads", payload);
        toast.success("Ad created");
      } else {
        await api.patch(`/admin/gcr/ads/${initial.ad_id}`, payload);
        toast.success("Ad updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="gcr-ad-form-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4" onClick={() => !saving && onClose()}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="relative w-full max-w-2xl bg-[#0f0f0f] border border-amber-500/30 rounded-xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <button type="button" onClick={() => !saving && onClose()} className="absolute top-3 right-3 text-gray-400 hover:text-white" disabled={saving} aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 mb-4">
          <Star className="w-6 h-6 text-amber-400" />
          <h3 className="text-xl font-black">{isNew ? "New banner ad" : `Edit "${initial.title}"`}</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Internal title *</label>
            <input data-testid="gcr-ad-form-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Witcher Premiere Banner" className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm" />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Banner image *</label>
            <div
              onDragEnter={onDragOver}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              data-testid="gcr-ad-form-dropzone"
              className={`flex gap-2 items-stretch rounded p-1 -m-1 transition-colors ${dragOver ? "ring-2 ring-amber-400/60 bg-amber-400/5" : ""}`}
            >
              <input data-testid="gcr-ad-form-image-url" required value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder={dragOver ? "Drop to upload…" : "https://… or upload / drop →"} className="flex-1 bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm" />
              <button type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()} className="btn-glass px-3 py-2 rounded text-xs whitespace-nowrap flex items-center gap-1.5">
                <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload"}
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" />
            </div>
            {form.image_url && (
              <div className="mt-2 rounded border border-white/10 overflow-hidden bg-black/40">
                <img src={form.image_url} alt="preview" className="w-full object-cover max-h-32" />
              </div>
            )}
            <p className="text-[10px] text-gray-500 mt-1">Recommended: 1600×200 (8:1) for top/bottom, 1200×400 (3:1) for middle. Tip: drag an image right onto this field.</p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Overlay caption (optional)</label>
            <input data-testid="gcr-ad-form-caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} placeholder="e.g. Witcher Season 4 — Tonight 9 PM EST" maxLength={140} className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm" />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Click destination URL</label>
            <input data-testid="gcr-ad-form-click-url" value={form.click_url} onChange={(e) => setForm({ ...form, click_url: e.target.value })} placeholder="/live/action  OR  https://external.com/promo" className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm" />
            <p className="text-[10px] text-gray-500 mt-1">Internal paths start with "/" and stay in-app. External links open in a new tab.</p>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Position</label>
            <select data-testid="gcr-ad-form-position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm">
              <option value="top">Top — above channel guide</option>
              <option value="middle">Middle — between sections</option>
              <option value="bottom">Bottom — below guide</option>
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Display order</label>
            <input data-testid="gcr-ad-form-order" type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value || "0", 10) })} className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Live from</label>
            <input data-testid="gcr-ad-form-starts" type="date" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Live until</label>
            <input data-testid="gcr-ad-form-ends" type="date" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="w-full bg-black/60 border border-white/15 focus:border-amber-500 outline-none rounded px-3 py-2 text-sm" />
          </div>
          <div className="md:col-span-2">
            <button type="button" data-testid="gcr-ad-form-active-toggle" onClick={() => setForm({ ...form, is_active: !form.is_active })} className={`w-full px-3 py-2 rounded text-sm font-semibold border ${form.is_active ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-white/5 border-white/15 text-gray-400"}`}>
              {form.is_active ? "Active — showing on /live now" : "Paused — hidden from /live"}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-white/10">
          <button type="button" onClick={onClose} disabled={saving} className="btn-glass px-5 py-2 rounded text-sm">Cancel</button>
          <button data-testid="gcr-ad-form-save" type="submit" disabled={saving || uploading} className="btn-red px-6 py-2 rounded text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
            {saving ? "Saving…" : (isNew ? "Create ad" : "Save changes")}
          </button>
        </div>
      </form>
    </div>
  );
}
